import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import type { Group } from 'three';

export interface LitterMeshProps {
  name: string;
  position: [number, number, number];
  collected?: boolean;
  highlight?: boolean;
  onTap: () => void;
}

const LITTER_COLORS = ['#E8A87C', '#C38D9E', '#41B3A3', '#E27D60', '#85CDCA', '#F6C945'];

/** Crumpled bag / scrap — reads as “tap me” trash, not a gray cube. */
export const LitterMesh: React.FC<LitterMeshProps> = ({
  name,
  position,
  collected = false,
  highlight = false,
  onTap,
}) => {
  const group = useRef<Group>(null);
  const color = LITTER_COLORS[Math.abs(name.length) % LITTER_COLORS.length];

  useFrame((state) => {
    if (!group.current || !highlight || collected) return;
    group.current.position.y = 0.08 + Math.sin(state.clock.elapsedTime * 3.2 + name.length) * 0.08;
  });

  if (collected) return null;

  return (
    <group ref={group} name={name} position={position}>
      <mesh
        name={`${name}-hit`}
        visible={false}
        position={[0, 0.4, 0]}
        onPointerDown={(e) => {
          e.stopPropagation();
          onTap();
        }}
      >
        <sphereGeometry args={[1.15, 12, 12]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      {highlight ? (
        <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.45, 0.75, 24]} />
          <meshBasicMaterial color="#FFE66D" transparent opacity={0.5} depthWrite={false} />
        </mesh>
      ) : null}

      {/* Bag body */}
      <mesh
        castShadow
        position={[0, 0.28, 0]}
        rotation={[0.25, 0.5, 0.15]}
        onPointerDown={(e) => {
          e.stopPropagation();
          onTap();
        }}
      >
        <boxGeometry args={[0.42, 0.32, 0.28]} />
        <meshStandardMaterial color={color} roughness={0.7} />
      </mesh>
      {/* Crumple bumps */}
      <mesh position={[0.12, 0.42, 0.05]} castShadow>
        <sphereGeometry args={[0.12, 8, 8]} />
        <meshStandardMaterial color={color} roughness={0.75} />
      </mesh>
      <mesh position={[-0.08, 0.38, -0.08]} castShadow>
        <sphereGeometry args={[0.1, 8, 8]} />
        <meshStandardMaterial color={color} roughness={0.75} />
      </mesh>

      {highlight ? (
        <mesh position={[0, 1.2, 0]} rotation={[Math.PI, 0, 0]}>
          <coneGeometry args={[0.2, 0.4, 4]} />
          <meshStandardMaterial color="#FFE66D" emissive="#FFE66D" emissiveIntensity={0.4} />
        </mesh>
      ) : null}
    </group>
  );
};

export default LitterMesh;
